import api from './api';
import { contentFromPath, AnalyticsEvents } from './analyticsEvents';
import { isBotBrowser } from '../utils/botDetection';

/**
 * First-Party-Analytics: schickt SPA-Pageviews ans EIGENE Backend
 * (POST /api/analytics/track) — zusätzlich zu GA, aber unabhängig vom
 * Cookie-Consent. Das ist DSGVO-konform möglich, weil:
 *
 * - KEINE persistente Client-ID: die Session-ID lebt nur im
 *   sessionStorage (Tab-Lebensdauer) und identifiziert niemanden
 *   über die Sitzung hinaus.
 * - Unique-Besucher bestimmt der SERVER per Tages-Hash — der Client
 *   liefert dafür keinerlei dauerhaften Identifier.
 * - Fail-soft: Tracking-Fehler dürfen NIE die App stören (nur
 *   console.debug, niemals werfen).
 *
 * Admin-Pfade (/admin/...) werden nicht getrackt — Eigenbesuche würden
 * die Statistik verzerren.
 */

export const SESSION_STORAGE_KEY = 'dc-analytics-session';

/** UUID v4 — crypto.randomUUID mit Math.random-Fallback (alte Browser/Jest) */
const generateId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* weiter zum Fallback */
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Session-ID: UUID im sessionStorage — stabil innerhalb der Browser-Session,
 * weg beim Schließen des Tabs. Bewusst KEIN localStorage (wäre ein
 * persistenter Identifier und damit consent-pflichtig).
 */
export const getSessionId = (): string => {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const id = generateId();
    sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    return id;
  } catch {
    // sessionStorage gesperrt → einmalige ID pro Aufruf (Tracking läuft trotzdem)
    return generateId();
  }
};

/**
 * page_view ans eigene Backend melden. userId ist optional und kommt vom
 * Aufrufer (AnalyticsTracker via useAuth) — der Service selbst liest
 * bewusst keine Auth-Daten.
 */
/** Gerätedaten wie beim page_view (einheitlich für alle Events) */
const deviceInfo = () => ({
  platform: 'web',
  userAgent: navigator.userAgent,
  screenWidth: window.screen?.width ?? null,
  screenHeight: window.screen?.height ?? null,
  language: navigator.language,
});

/**
 * Beliebiges Katalog-Event (2026-09-22) ans eigene Backend melden – z. B.
 * listing_follow, search, prediction_submit, sponsor_banner_click. Fail-soft.
 * Aufrufer aus Services/Komponenten; userId wird aus dem gespeicherten Login gelesen.
 */
export const trackEvent = async (
  eventName: string,
  parameters: Record<string, unknown> = {},
  userId?: string | null,
): Promise<void> => {
  try {
    if (!eventName) return;
    // Bot-Filter (2026-09-25): Crawler führen unser JS aus – nichts senden
    if (isBotBrowser()) return;
    const path = window.location.pathname;
    if (path === '/admin' || path.startsWith('/admin/')) return;
    await api.post('/api/analytics/track', {
      eventName,
      sessionId: getSessionId(),
      userId: userId ?? readStoredUserId(),
      deviceInfo: deviceInfo(),
      url: window.location.href,
      referrer: document.referrer || null,
      parameters: { page_path: path, ...parameters },
    });
  } catch (err) {
    console.debug(`firstPartyAnalytics: ${eventName} nicht gesendet`, err);
  }
};

/** Nutzer-ID aus dem gespeicherten Login (dc-user), ohne Auth-Kontext – null wenn nicht eingeloggt */
const readStoredUserId = (): string | null => {
  try {
    const raw = window.localStorage?.getItem('dc-user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const id = parsed?.id ?? parsed?._id ?? null;
    return typeof id === 'string' && id ? id : null;
  } catch {
    return null;
  }
};

export const trackPageView = async (
  path: string,
  title?: string,
  userId?: string | null
): Promise<void> => {
  try {
    if (!path || path === '/admin' || path.startsWith('/admin/')) return;
    if (isBotBrowser()) return; // Bot-Filter 2026-09-25
    await api.post('/api/analytics/track', {
      eventName: 'page_view',
      sessionId: getSessionId(),
      userId: userId ?? null,
      deviceInfo: deviceInfo(),
      url: window.location.href,
      referrer: document.referrer || null,
      parameters: {
        page_path: path,
        page_title: title ?? document.title,
        // Inhalts-Kontext (2026-09-22): Typ + Slug aus dem Pfad, für Auswertungen je Inhalt
        ...contentFromPath(path),
      },
    });
  } catch (err) {
    // Fail-soft: Tracking darf die App NIE stören
    console.debug('firstPartyAnalytics: page_view nicht gesendet', err);
  }
};

/** Teilen-Event mit Inhalts-Kontext aus dem aktuellen Pfad (nativ / Link kopiert) */
export const trackShare = (channel: 'native' | 'copy' | string = 'native'): void => {
  const ctx = contentFromPath(window.location.pathname);
  void trackEvent(AnalyticsEvents.share, { content_type: ctx.content_type, content_id: ctx.content_slug, channel });
};

const firstPartyAnalytics = { getSessionId, trackPageView, trackEvent, trackShare };
export default firstPartyAnalytics;
